import React, { useState, useEffect, createElement } from 'react';
import {
    BeakerIcon,
    ScaleIcon,
    CheckBadgeIcon,
    BanknotesIcon,
    ShoppingCartIcon,
    CalculatorIcon,
    PlusIcon,
    ArrowDownTrayIcon,
    ArrowPathIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    XMarkIcon,
    StarIcon,
    TruckIcon,
    ClockIcon,
    FireIcon,
    SparklesIcon,
    ChevronRightIcon,
    UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import CompostBatchesTable from '../components/compost/CompostBatchesTable';
import MaterialStockWorkspace from '../components/compost/MaterialStockWorkspace';
import BatchDetailModal from '../components/compost/BatchDetailModal';
import QualityControlForm from '../components/compost/QualityControlForm';
import StipendManagement from '../components/compost/StipendManagement';
import SurplusSalesTracker from '../components/compost/SurplusSalesTracker';
import LaborAssignmentModal from '../components/compost/LaborAssignmentModal';
import ProfessionalBatchModal from '../components/compost/ProfessionalBatchModal';
import { CompostBatch } from '../types/dashboard.types';
import { apiGet, apiPost, apiPatch } from '../utils/api';
import { useCurrency } from '../hooks/useCurrency';

// ─── Types ────────────────────────────────────────────────────────────────────
type BatchStatus = 'in_progress' | 'curing' | 'mature' | 'sold';
type QualityGrade = 'premium' | 'good' | 'fair' | 'poor';

// ─── Config ───────────────────────────────────────────────────────────────────
const BATCH_STATUS: Record<string, { label: string; color: string; bg: string; dot: string; border: string }> = {
    in_progress: { label: 'In Progress', color: 'text-blue-700', bg: 'bg-blue-50', dot: 'bg-blue-500', border: 'border-blue-200' },
    curing: { label: 'Curing', color: 'text-amber-700', bg: 'bg-amber-50', dot: 'bg-amber-500', border: 'border-amber-200' },
    mature: { label: 'Mature', color: 'text-green-700', bg: 'bg-green-50', dot: 'bg-green-500', border: 'border-green-200' },
    sold: { label: 'Sold', color: 'text-purple-700', bg: 'bg-purple-50', dot: 'bg-purple-500', border: 'border-purple-200' },
};

const getQualityGrade = (score: number | null): { grade: QualityGrade; label: string; color: string } => {
    if (!score) return { grade: 'poor', label: 'Unscored', color: 'text-gray-500' };
    if (score >= 90) return { grade: 'premium', label: 'Premium', color: 'text-emerald-700' };
    if (score >= 80) return { grade: 'good', label: 'Good', color: 'text-green-700' };
    if (score >= 70) return { grade: 'fair', label: 'Fair', color: 'text-amber-700' };
    return { grade: 'poor', label: 'Poor', color: 'text-red-700' };
};
 
// ─── Sub-components ───────────────────────────────────────────────────────────
const GradientKPI: React.FC<{
    title: string; value: string | number; sub?: string;
    from: string; to: string; icon: React.ReactNode;
}> = ({ title, value, sub, from, to, icon }) => (
    <div className={`bg-gradient-to-br ${from} ${to} p-5 rounded-2xl text-white shadow-lg`}>
        <div className="flex items-start justify-between">
            <div className="p-2 bg-white/20 rounded-xl">{icon}</div>
        </div>
        <p className="text-3xl font-extrabold mt-3">{value}</p>
        <p className="text-white/80 text-sm font-semibold mt-0.5">{title}</p>
        {sub && <p className="text-white/60 text-xs mt-0.5">{sub}</p>}
    </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const cfg = BATCH_STATUS[status] || { label: status, color: 'text-gray-700', bg: 'bg-gray-100', dot: 'bg-gray-400', border: 'border-gray-200' };
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
};

// ─── Create Batch Modal ───────────────────────────────────────────────────────
const CreateBatchModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
    const [form, setForm] = useState({
        production_date: new Date().toISOString().split('T')[0],
        maturity_date: '',
        quantity_kg: '',
        location: '',
        notes: '',
        feedstock: [
            { material: 'Maize Stalks', pct: 40, kg: '' },
            { material: 'Banana Leaves', pct: 30, kg: '' },
            { material: 'Coffee Pulp', pct: 20, kg: '' },
            { material: 'Avocado Prunings', pct: 10, kg: '' },
        ],
    });
    const [submitting, setSubmitting] = useState(false);

    const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    const totalPct = form.feedstock.reduce((s, f) => s + (f.pct || 0), 0);

    const handleSubmit = async () => {
        if (!form.production_date || !form.quantity_kg) return alert('Production date and quantity are required.');
        setSubmitting(true);
        try {
            await apiPost('/api/compost/batches', {
                cohort_id: null,
                production_date: form.production_date,
                maturity_date: form.maturity_date || null,
                quantity_kg: parseFloat(form.quantity_kg),
                location: form.location,
                notes: form.notes,
            });
            onCreated();
            onClose();
        } catch {
            alert('Failed to create batch. Please try again.');
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-700 to-emerald-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="text-white">
                        <h2 className="text-lg font-bold">Create New Compost Batch</h2>
                        <p className="text-green-100 text-sm">Fill in batch details and feedstock composition</p>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white"><XMarkIcon className="w-6 h-6" /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {/* Batch Info */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Batch Information</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Quantity (kg) *</label>
                                <input type="number" min={1} value={form.quantity_kg} onChange={set('quantity_kg')} placeholder="e.g. 500"
                                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Production Date *</label>
                                <input type="date" value={form.production_date} onChange={set('production_date')}
                                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Expected Maturity Date</label>
                                <input type="date" value={form.maturity_date} onChange={set('maturity_date')}
                                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Location / Facility</label>
                                <input value={form.location} onChange={set('location')} placeholder="e.g. Farm Site A, Pit 3"
                                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                            </div>
                        </div>
                    </div>

                    {/* Feedstock */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Feedstock Composition</p>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${totalPct === 100 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {totalPct}% of 100%
                            </span>
                        </div>
                        <div className="space-y-2">
                            {form.feedstock.map((item, idx) => (
                                <div key={item.material} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                    <span className="text-sm font-semibold text-gray-700 w-36 flex-shrink-0">{item.material}</span>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <input type="number" value={item.pct} min={0} max={100}
                                                onChange={e => setForm(f => ({ ...f, feedstock: f.feedstock.map((fi, i) => i === idx ? { ...fi, pct: +e.target.value } : fi) }))}
                                                className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center" />
                                            <span className="text-gray-500 text-xs">%</span>
                                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                <div className="bg-green-500 h-full rounded-full" style={{ width: `${Math.min(item.pct, 100)}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
                        <textarea value={form.notes} onChange={set('notes')} rows={2}
                            placeholder="Initial conditions, observations..."
                            className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none" />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3 flex-shrink-0">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
                    <button onClick={handleSubmit} disabled={submitting}
                        className="px-5 py-2 bg-gradient-to-r from-green-700 to-emerald-600 text-white rounded-xl text-sm font-semibold shadow hover:shadow-md transition-all disabled:opacity-60">
                        {submitting ? 'Creating...' : 'Create Batch'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Analytics Panel (Manager only) ──────────────────────────────────────────
const AddManureSourceModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
    const [form, setForm] = useState({
        vendor_id: '',
        supplier_name: '',
        supplier_type: 'Dairy Farm',
        material_type: 'Cow Manure',
        quantity_kg: '',
        collection_date: new Date().toISOString().split('T')[0],
        location: '',
        cost_per_kg: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [vendors, setVendors] = useState<any[]>([]);
    const [newVendorName, setNewVendorName] = useState('');
    const [vendorError, setVendorError] = useState('');
    const [vendorBusy, setVendorBusy] = useState(false);

    useEffect(() => {
        apiGet<any>('/api/compost/vendors').then((result) => setVendors(result?.vendors || [])).catch(() => setVendors([]));
    }, []);

    const createVendor = async () => {
        if (!newVendorName.trim()) return;
        setVendorBusy(true);
        setVendorError('');
        try {
            const result = await apiPost<any>('/api/compost/vendors', {
                display_name: newVendorName.trim(),
                product_categories: ['manure'],
            });
            const vendor = result?.vendor;
            if (vendor) {
                setVendors((current) => [...current, vendor].sort((a, b) => a.display_name.localeCompare(b.display_name)));
                setForm((current) => ({ ...current, vendor_id: String(vendor.id), supplier_name: vendor.display_name }));
                setNewVendorName('');
            }
        } catch (error: any) {
            setVendorError(error?.message || 'Could not create supplier. Confirm migration 080 is applied.');
        } finally {
            setVendorBusy(false);
        }
    };

    const handleSubmit = async () => {
        if (!form.vendor_id || !form.quantity_kg) {
            setVendorError('Select an external supplier and enter the quantity.');
            return;
        }
        setSubmitting(true);
        try {
            await apiPost('/api/compost/material-sources', {
                ...form,
                vendor_id: Number(form.vendor_id),
                quantity_kg: parseFloat(form.quantity_kg),
                cost_per_kg: parseFloat(form.cost_per_kg) || 0,
            });
            onSuccess();
            onClose();
        } catch {
            alert('Failed to add material source');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
                <div className="bg-gradient-to-r from-amber-700 to-orange-600 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-white">Add Manure/Material Source</h2>
                    <button onClick={onClose} className="text-white/80 hover:text-white"><XMarkIcon className="w-6 h-6" /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">External supplier *</label>
                            <select value={form.vendor_id} onChange={(e) => {
                                const vendor = vendors.find((item) => String(item.id) === e.target.value);
                                setForm({ ...form, vendor_id: e.target.value, supplier_name: vendor?.display_name || '' });
                            }} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm">
                                <option value="">Choose a workspace supplier…</option>
                                {vendors.filter((item) => !item.is_archived).map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.display_name}</option>)}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">Suppliers are external businesses, not farmer records or workspace users.</p>
                            <div className="mt-2 flex gap-2">
                                <input value={newVendorName} onChange={(e) => setNewVendorName(e.target.value)} placeholder="Add a new supplier" className="min-w-0 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                                <button type="button" onClick={createVendor} disabled={vendorBusy || !newVendorName.trim()} className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">{vendorBusy ? 'Adding…' : 'Add supplier'}</button>
                            </div>
                            {vendorError && <p className="mt-2 text-xs font-semibold text-red-700">{vendorError}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Supplier Type</label>
                            <select value={form.supplier_type} onChange={(e) => setForm({ ...form, supplier_type: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm">
                                <option>Dairy Farm</option>
                                <option>Poultry Farm</option>
                                <option>Pig Farm</option>
                                <option>Goat Farm</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Material Type</label>
                            <select value={form.material_type} onChange={(e) => setForm({ ...form, material_type: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm">
                                <option>Cow Manure</option>
                                <option>Chicken Manure</option>
                                <option>Pig Manure</option>
                                <option>Goat Manure</option>
                                <option>Dry Leaves</option>
                                <option>Crop Residues</option>
                                <option>Straw</option>
                                <option>Sawdust</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Quantity (kg) *</label>
                            <input type="number" value={form.quantity_kg} onChange={(e) => setForm({ ...form, quantity_kg: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Collection Date</label>
                            <input type="date" value={form.collection_date} onChange={(e) => setForm({ ...form, collection_date: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
                            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Cost per kg (RWF)</label>
                            <input type="number" value={form.cost_per_kg} onChange={(e) => setForm({ ...form, cost_per_kg: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
                    <button onClick={handleSubmit} disabled={submitting}
                        className="px-5 py-2 bg-gradient-to-r from-amber-700 to-orange-600 text-white rounded-xl text-sm font-semibold">
                        {submitting ? 'Adding...' : 'Add Source'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AddDistributionModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
    const [farmers, setFarmers] = useState<any[]>([]);
    const [selectedFarmer, setSelectedFarmer] = useState<any>(null);
    const [compostReceived, setCompostReceived] = useState('');
    const [distributionDate, setDistributionDate] = useState(new Date().toISOString().split('T')[0]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        apiGet('/api/farmers').then((d: any) => {
            const rows = Array.isArray(d) ? d : (Array.isArray(d?.farmers) ? d.farmers : []);
            setFarmers(rows);
        }).catch(() => {});
    }, []);

    const recommendedCompost = selectedFarmer?.plot_size_hectares && selectedFarmer?.primary_crop
        ? parseFloat(selectedFarmer.plot_size_hectares) * 
          (selectedFarmer.primary_crop === 'Maize' ? 5000 : 
           selectedFarmer.primary_crop === 'Beans' ? 3000 : 
           selectedFarmer.primary_crop === 'Vegetables' ? 8000 : 5000)
        : 0;

    const handleSubmit = async () => {
        if (!selectedFarmer || !compostReceived) {
            alert('Please select a farmer and enter compost amount');
            return;
        }
        setSubmitting(true);
        try {
            await apiPost('/api/compost/distributions', {
                farmer_id: selectedFarmer.id,
                farmer_name: selectedFarmer.full_name,
                farm_size_hectares: parseFloat(selectedFarmer.plot_size_hectares || 0),
                crop_type: selectedFarmer.primary_crop || 'Maize',
                compost_received_kg: parseFloat(compostReceived),
                distribution_date: distributionDate,
                cohort_id: null,
            });
            onSuccess();
            onClose();
        } catch {
            alert('Failed to record distribution');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl">
                <div className="bg-gradient-to-r from-blue-700 to-indigo-600 px-6 py-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-white">Record Compost Distribution</h2>
                        <p className="text-blue-100 text-sm">Select farmer and record compost delivered</p>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white"><XMarkIcon className="w-6 h-6" /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Farmer *</label>
                            <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-xl">
                                {farmers.length === 0 ? (
                                    <div className="p-8 text-center text-gray-500">No farmers found</div>
                                ) : (
                                    <div className="divide-y divide-gray-200">
                                        {farmers.map(farmer => (
                                                <div key={farmer.id}
                                                    onClick={() => setSelectedFarmer(farmer)}
                                                    className={`p-4 cursor-pointer hover:bg-blue-50 transition-colors ${
                                                        selectedFarmer?.id === farmer.id ? 'bg-blue-100 border-l-4 border-blue-600' : ''
                                                    }`}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <img src={farmer.photo_url || 'https://via.placeholder.com/40'}
                                                                alt={farmer.full_name}
                                                                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                                                                onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40?text=User'; }} />
                                                            <div>
                                                                <p className="font-semibold text-gray-900">{farmer.full_name}</p>
                                                                <p className="text-sm text-gray-600">{farmer.phone_number}</p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-sm font-semibold text-gray-900">{Number(farmer.plot_size_hectares || 0).toFixed(2)} ha</p>
                                                            <p className="text-xs text-gray-600">{farmer.primary_crop || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        {selectedFarmer && recommendedCompost > 0 && (
                            <div className="col-span-2 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-semibold text-blue-900">Recommended Compost</p>
                                        <p className="text-xs text-blue-700 mt-1">
                                            {selectedFarmer.plot_size_hectares} ha × {selectedFarmer.primary_crop} requirement
                                        </p>
                                    </div>
                                    <p className="text-3xl font-bold text-blue-700">{recommendedCompost.toLocaleString()} kg</p>
                                </div>
                            </div>
                        )}
                        {selectedFarmer && (
                            <>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Compost Received (kg) *</label>
                                    <input type="number" value={compostReceived}
                                        onChange={(e) => setCompostReceived(e.target.value)}
                                        placeholder="Enter amount"
                                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Distribution Date</label>
                                    <input type="date" value={distributionDate}
                                        onChange={(e) => setDistributionDate(e.target.value)}
                                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 text-sm text-gray-600 hover:text-gray-900 font-medium">Cancel</button>
                    <button onClick={handleSubmit} disabled={submitting || !selectedFarmer}
                        className="px-6 py-2.5 bg-gradient-to-r from-blue-700 to-indigo-600 text-white rounded-xl text-sm font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                        {submitting ? 'Recording...' : 'Record Distribution'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Analytics Panel (Manager only) ──────────────────────────────────────────
const AnalyticsPanel: React.FC<{ batches: any[]; sales: any[]; summary: any; formatCurrency: (amount: number) => string }> = ({ batches, sales, summary, formatCurrency }) => {
    const statusBreakdown = Object.entries(BATCH_STATUS).map(([k, cfg]) => ({
        status: k, cfg,
        count: batches.filter(b => b.status === k).length,
        pct: batches.length > 0 ? Math.round((batches.filter(b => b.status === k).length / batches.length) * 100) : 0,
    }));

    const qualityBuckets = [
        { label: 'Premium (90-100)', count: batches.filter(b => b.qualityScore >= 90).length, color: 'bg-emerald-500' },
        { label: 'Good (80-89)', count: batches.filter(b => b.qualityScore >= 80 && b.qualityScore < 90).length, color: 'bg-green-500' },
        { label: 'Fair (70-79)', count: batches.filter(b => b.qualityScore >= 70 && b.qualityScore < 80).length, color: 'bg-amber-500' },
        { label: 'Poor / Unscored', count: batches.filter(b => !b.qualityScore || b.qualityScore < 70).length, color: 'bg-red-400' },
    ];

    const totalSalesRevenue = sales.reduce((s, r) => s + (r.totalRevenue || 0), 0);

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {/* Batch Status Breakdown */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Batch Status Breakdown</p>
                <div className="space-y-3">
                    {statusBreakdown.map(({ status, cfg, count, pct }) => (
                        <div key={status} className="flex items-center gap-3">
                            <span className={`text-xs font-semibold w-20 ${cfg.color}`}>{cfg.label}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-2">
                                <div className={`h-full rounded-full ${cfg.dot}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-gray-500 w-12 text-right">{count} ({pct}%)</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quality Distribution */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Quality Distribution</p>
                <div className="space-y-3">
                    {qualityBuckets.map(({ label, count, color }) => {
                        const pct = batches.length > 0 ? Math.round((count / batches.length) * 100) : 0;
                        return (
                            <div key={label} className="flex items-center gap-3">
                                <span className="text-xs text-gray-600 w-28 leading-tight">{label}</span>
                                <div className="flex-1 bg-gray-100 rounded-full h-2">
                                    <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs text-gray-500 w-12 text-right">{count} ({pct}%)</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Financial Summary</p>
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Labour Paid</span>
                        <span className="text-sm font-bold text-gray-900">
                            {formatCurrency(summary?.total_stipends_paid || 0)}
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Surplus Sales Revenue</span>
                        <span className="text-sm font-bold text-green-700">
                            {formatCurrency(totalSalesRevenue)}
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Cost per kg</span>
                        <span className={`text-sm font-bold ${Number(summary?.cost_per_kg || 0) <= 200 ? 'text-green-700' : 'text-amber-700'}`}>
                            {formatCurrency(Math.round(Number(summary?.cost_per_kg || 0)))}
                        </span>
                    </div>
                    <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">Net Position</span>
                        <span className="text-sm font-extrabold text-blue-700">
                            {formatCurrency(Math.max(0, totalSalesRevenue - (summary?.total_stipends_paid || 0)))}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const CompostPage: React.FC = () => {
    const { user } = useAuth();
    const { format: formatCurrency } = useCurrency();
    const isManager = user?.role === 'project_manager' || user?.role === 'agronomist';

    // State management
    const [batches, setBatches] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [stipends, setStipends] = useState<any[]>([]);
    const [salesData, setSalesData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // NEW: Industrial compost production state
    const [manureSources, setManureSources] = useState<any[]>([]);
    const [windrows, setWindrows] = useState<any[]>([]);
    const [distributions, setDistributions] = useState<any[]>([]);
    const [analytics, setAnalytics] = useState<any>(null);

    // Tab navigation - ENHANCED with new tabs
    const [activeTab, setActiveTab] = useState<'batches' | 'inputs' | 'labor' | 'sales' | 'manure' | 'production' | 'distribution' | 'analytics'>('batches');

    // Filters
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');

    // Modals
    const [selectedBatch, setSelectedBatch] = useState<CompostBatch | null>(null);
    const [qualityFormBatch, setQualityFormBatch] = useState<CompostBatch | null>(null);
    const [laborModalBatch, setLaborModalBatch] = useState<{ id: string; batchNumber: string } | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showManureModal, setShowManureModal] = useState(false);
    const [showDistributionModal, setShowDistributionModal] = useState(false);

    useEffect(() => { fetchAllData(); }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [batchesData, summaryData, stipendsData, salesHistory, manureData, windrowData, distributionData, analyticsData] = await Promise.all([
                apiGet<any[]>('/api/compost/batches/detailed'),
                apiGet<any>('/api/compost/dashboard/summary'),
                apiGet<any[]>('/api/compost/payments/pending'),
                apiGet<any[]>('/api/compost/sales'),
                apiGet<any[]>('/api/compost/material-sources'),
                apiGet<any[]>('/api/compost/windrows'),
                apiGet<any[]>('/api/compost/distributions'),
                apiGet<any>('/api/compost/analytics')
            ]);

            const mappedBatches = batchesData.map((b: any) => ({
                id: b.id,
                batchNumber: b.batch_number || b.batchNumber,
                feedstockMix: b.feedstock_mix || b.feedstockMix || [],
                startDate: b.production_date || b.startDate,
                maturityDate: b.maturity_date || b.maturityDate,
                kgProduced: Number(b.quantity_kg || b.kgProduced || 0),
                qualityScore: b.quality_score ? Number(b.quality_score) : null,
                status: b.status,
                cohortId: b.cohort_id || b.cohortId,
                cohortName: b.cohort_name || b.cohortName || '—',
                fieldName: b.field_name || null,
                fieldCropType: b.field_crop_type || null,
                farmerName: b.farmer_name || null,
                fieldPlotId: b.field_plot_id || null,
                farmerId: b.farmer_id || null,
                createdBy: b.produced_by || b.createdBy,
                createdAt: b.created_at || b.createdAt,
                updatedAt: b.updated_at || b.updatedAt,
            }));

            const mappedStipends = stipendsData.map((s: any) => ({
                id: s.id,
                farmerName: s.worker_name,
                batchId: s.batch_number,
                daysWorked: 1,
                tasks: ['General Labor'],
                baseStipend: Number(s.daily_wage),
                qualityBonus: 0,
                totalStipend: Number(s.daily_wage),
                status: s.payment_status === 'pending' ? 'Pending' : 'Paid',
            }));

            const mappedSales = salesHistory.map((s: any) => ({
                id: s.id,
                saleDate: s.sale_date,
                buyerName: s.buyer_name,
                buyerContact: s.buyer_contact,
                kgSold: Number(s.kg_sold),
                pricePerKg: Number(s.price_per_kg),
                totalRevenue: Number(s.kg_sold) * Number(s.price_per_kg),
                paymentMethod: s.payment_method,
            }));

            setBatches(mappedBatches);
            setSummary(summaryData);
            setStipends(mappedStipends);
            setSalesData(mappedSales);
            setManureSources(Array.isArray(manureData) ? manureData : []);
            setWindrows(Array.isArray(windrowData) ? windrowData : []);
            setDistributions(Array.isArray(distributionData) ? distributionData : []);
            setAnalytics(analyticsData);
        } catch (error) {
            console.error('Failed to fetch compost data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filtering
    const filteredBatches = batches.filter(b => {
        if (statusFilter !== 'all' && b.status !== statusFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            if (!((b.batchNumber || '').toLowerCase().includes(q) || (b.cohortName || '').toLowerCase().includes(q))) return false;
        }
        return true;
    });

    const totalKg = batches.reduce((s, b) => s + (b.kgProduced || 0), 0);
    const avgQuality = batches.filter(b => b.qualityScore).length > 0
        ? (batches.filter(b => b.qualityScore).reduce((s, b) => s + b.qualityScore, 0) / batches.filter(b => b.qualityScore).length)
        : 0;
    const totalSalesRevenue = salesData.reduce((s, r) => s + (r.totalRevenue || 0), 0);
    const matureBatches = batches.filter(b => b.status === 'mature').length;

    const handleExport = () => {
        const csv = batches.map(b => `${b.batchNumber},${b.cohortName},${b.kgProduced},${b.status},${b.qualityScore || '—'},${b.startDate}`).join('\n');
        const blob = new Blob([`Batch #,Cohort,Qty (kg),Status,Quality,Production Date\n${csv}`], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compost_batches_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    const tabs = [
        { id: 'batches', label: 'Compost Batches', icon: BeakerIcon, count: batches.length },
        { id: 'inputs', label: 'Inputs & Stock', icon: ScaleIcon, count: 0 },
        { id: 'manure', label: 'Manure Supply', icon: TruckIcon, count: manureSources.length },
        { id: 'production', label: 'Production Engineering', icon: FireIcon, count: windrows.length },
        { id: 'distribution', label: 'Farmer Distribution', icon: UserGroupIcon, count: distributions.length },
        { id: 'labor', label: 'Labor Management', icon: BanknotesIcon, count: stipends.length },
        { id: 'sales', label: 'Surplus Sales', icon: ShoppingCartIcon, count: salesData.length },
        { id: 'analytics', label: 'Analytics', icon: SparklesIcon, count: 0 },
    ];

    if (loading && batches.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="mt-4 text-gray-500 font-medium">Loading compost data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50/30 p-4 md:p-6 space-y-6">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── HEADER ── */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-3">
                            <span className="p-2 bg-green-100 rounded-xl">
                                <BeakerIcon className="w-8 h-8 text-green-700" />
                            </span>
                            Compost Production
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            {batches.length} Batches &nbsp;•&nbsp; {totalKg.toLocaleString()} kg Produced &nbsp;•&nbsp;
                            {matureBatches} Ready for Distribution &nbsp;•&nbsp; {formatCurrency(totalSalesRevenue)} Surplus Revenue
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={() => fetchAllData()}
                            className="px-3 py-2 border border-gray-300 rounded-xl text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2">
                            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                        </button>
                        <button onClick={handleExport}
                            className="px-3 py-2 border border-gray-300 rounded-xl text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2">
                            <ArrowDownTrayIcon className="w-4 h-4" /> Export CSV
                        </button>
                        <button onClick={() => setShowCreateModal(true)}
                            className="px-5 py-2 bg-gradient-to-r from-green-700 to-emerald-600 text-white rounded-xl text-sm font-semibold shadow-lg hover:shadow-xl flex items-center gap-2 transition-all">
                            <PlusIcon className="w-4 h-4" /> New Batch
                        </button>
                    </div>
                </div>

                {/* ── KPI CARDS ── */}
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                    <GradientKPI title="Total Batches" value={batches.length}
                        sub={`${batches.filter(b => b.status === 'in_progress').length} active`}
                        from="from-slate-700" to="to-slate-900"
                        icon={<BeakerIcon className="w-5 h-5 text-white" />} />
                    <GradientKPI title="Total Produced" value={`${totalKg.toLocaleString()} kg`}
                        sub={`${matureBatches} batches mature`}
                        from="from-green-600" to="to-emerald-700"
                        icon={<ScaleIcon className="w-5 h-5 text-white" />} />
                    <GradientKPI title="Avg Quality"
                        value={avgQuality > 0 ? `${avgQuality.toFixed(1)}/100` : '—'}
                        sub={avgQuality >= 80 ? 'Good+ quality' : avgQuality >= 70 ? 'Fair quality' : 'Needs focus'}
                        from={avgQuality >= 85 ? 'from-emerald-500' : avgQuality >= 70 ? 'from-amber-500' : 'from-red-500'}
                        to={avgQuality >= 85 ? 'to-green-700' : avgQuality >= 70 ? 'to-orange-600' : 'to-red-700'}
                        icon={<CheckBadgeIcon className="w-5 h-5 text-white" />} />
                    <GradientKPI title="Labour Paid"
                        value={formatCurrency(summary?.total_stipends_paid || 0)}
                        sub={`${stipends.length} pending records`}
                        from="from-indigo-600" to="to-purple-700"
                        icon={<BanknotesIcon className="w-5 h-5 text-white" />} />
                    <GradientKPI title="Cost / kg"
                        value={formatCurrency(Math.round(Number(summary?.cost_per_kg || 0)))}
                        sub={Number(summary?.cost_per_kg || 0) <= 200 ? 'Within target' : 'Above target'}
                        from={Number(summary?.cost_per_kg || 0) <= 200 ? 'from-green-600' : 'from-amber-500'}
                        to={Number(summary?.cost_per_kg || 0) <= 200 ? 'to-green-800' : 'to-orange-600'}
                        icon={<CalculatorIcon className="w-5 h-5 text-white" />} />
                    <GradientKPI title="Surplus Revenue"
                        value={formatCurrency(totalSalesRevenue)}
                        sub={`${salesData.length} sales recorded`}
                        from="from-rose-500" to="to-pink-700"
                        icon={<ShoppingCartIcon className="w-5 h-5 text-white" />} />
                </div>

                {/* ── BATCH PIPELINE ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Production Pipeline</p>
                    <div className="grid grid-cols-4 gap-4">
                        {Object.entries(BATCH_STATUS).map(([k, cfg]) => {
                            const count = batches.filter(b => b.status === k).length;
                            const totalKgForStatus = batches.filter(b => b.status === k).reduce((s, b) => s + (b.kgProduced || 0), 0);
                            return (
                                <button key={k}
                                    onClick={() => setStatusFilter(statusFilter === k ? 'all' : k)}
                                    className={`rounded-xl p-4 border-2 transition-all cursor-pointer flex flex-col items-center ${statusFilter === k ? 'border-green-500 bg-green-50' : cfg.border + ' ' + cfg.bg + ' hover:shadow-sm'}`}>
                                    <p className={`text-3xl font-extrabold ${cfg.color}`}>{count}</p>
                                    <p className={`text-sm font-bold mt-1 ${cfg.color}`}>{cfg.label}</p>
                                    <p className="text-xs text-gray-400 mt-1">{totalKgForStatus.toLocaleString()} kg</p>
                                    <ChevronRightIcon className={`w-4 h-4 mt-2 ${cfg.color} opacity-50`} />
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ── TABS ── */}
                <div className="bg-white rounded-t-2xl border border-gray-100 shadow-sm">
                    <div className="flex space-x-1 px-4 pt-4">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button key={tab.id}
                                    onClick={() => setActiveTab(tab.id as any)}
                                    className={`flex items-center gap-2 px-4 py-3 rounded-t-xl text-sm font-semibold border-b-2 transition-all ${isActive
                                            ? 'border-green-600 text-green-700 bg-green-50'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                        }`}>
                                    <Icon className="w-4 h-4" />
                                    {tab.label}
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${isActive ? 'bg-green-200 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                        {tab.count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ── TAB CONTENT ── */}
                <div className="bg-white rounded-b-2xl shadow-sm border border-t-0 border-gray-100 -mt-6 pt-0">
                    {/* Search & Filter (Batches tab only) */}
                    {activeTab === 'batches' && (
                        <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row gap-3">
                            <div className="flex-1 relative">
                                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input value={search} onChange={e => setSearch(e.target.value)}
                                    placeholder="Search by batch number or cohort..."
                                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-300" />
                            </div>
                            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300">
                                <option value="all">All Statuses</option>
                                {Object.entries(BATCH_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                            {(statusFilter !== 'all' || search) && (
                                <button onClick={() => { setStatusFilter('all'); setSearch(''); }}
                                    className="px-3 py-2 border border-red-200 text-red-600 rounded-xl text-sm hover:bg-red-50 flex items-center gap-1">
                                    <XMarkIcon className="w-4 h-4" /> Clear
                                </button>
                            )}
                            <div className="flex items-center gap-2 text-sm text-gray-500 pl-2">
                                <FunnelIcon className="w-4 h-4" />
                                <span className="font-semibold text-gray-700">{filteredBatches.length}</span> results
                            </div>
                        </div>
                    )}

                    <div className="p-4">
                        {activeTab === 'batches' && (
                            <CompostBatchesTable
                                batches={filteredBatches}
                                onViewDetails={setSelectedBatch}
                                onRecordQuality={setQualityFormBatch}
                                onPayStipends={(batch) => setLaborModalBatch({
                                    id: batch.id,
                                    batchNumber: batch.batchNumber || (batch as any).batch_number || `BATCH-${batch.id}`
                                })}
                            />
                        )}
                        {activeTab === 'inputs' && (
                            <MaterialStockWorkspace />
                        )}
                        {activeTab === 'manure' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Manure & Material Sources</h3>
                                        <p className="text-sm text-gray-600 mt-1">Track animal waste and carbon material collection</p>
                                    </div>
                                    <button
                                        onClick={() => setShowManureModal(true)}
                                        className="px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:shadow-lg flex items-center gap-2 font-semibold transition-all"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        Add Source
                                    </button>
                                </div>
                                
                                {/* Summary Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-xl border-2 border-amber-200">
                                        <p className="text-sm text-amber-700 font-semibold">Total Manure Collected</p>
                                        <p className="text-3xl font-bold text-amber-900 mt-2">
                                            {manureSources.filter(s => s.material_type.includes('Manure')).reduce((sum, s) => sum + Number(s.quantity_kg), 0).toLocaleString()} kg
                                        </p>
                                    </div>
                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border-2 border-green-200">
                                        <p className="text-sm text-green-700 font-semibold">Total Carbon Materials</p>
                                        <p className="text-3xl font-bold text-green-900 mt-2">
                                            {manureSources.filter(s => !s.material_type.includes('Manure')).reduce((sum, s) => sum + Number(s.quantity_kg), 0).toLocaleString()} kg
                                        </p>
                                    </div>
                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border-2 border-blue-200">
                                        <p className="text-sm text-blue-700 font-semibold">Total Suppliers</p>
                                        <p className="text-3xl font-bold text-blue-900 mt-2">{new Set(manureSources.map(s => s.supplier_name)).size}</p>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gradient-to-r from-amber-50 to-orange-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Supplier</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Type</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Material</th>
                                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Quantity</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Location</th>
                                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Cost/kg</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {manureSources.map((source) => (
                                                    <tr key={source.id} className="hover:bg-amber-50 transition-colors">
                                                        <td className="px-4 py-3 text-sm font-bold text-gray-900">{source.supplier_name}</td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                                                {source.supplier_type}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                                source.material_type.includes('Manure') ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                                                            }`}>
                                                                {source.material_type}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{Number(source.quantity_kg).toLocaleString()} kg</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">{source.collection_date}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">{source.location}</td>
                                                        <td className="px-4 py-3 text-sm text-right font-semibold">{formatCurrency(source.cost_per_kg)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'production' && (
                            <div className="space-y-6">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-bold text-gray-900">Compost Production Engineering</h3>
                                    <button
                                        onClick={() => setShowCreateModal(true)}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 shadow-lg"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        Create Batch
                                    </button>
                                </div>
                                
                                {/* Batch Production Table */}
                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gradient-to-r from-green-50 to-emerald-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Batch #</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Field</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Farmer</th>
                                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Manure</th>
                                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Carbon</th>
                                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Water</th>
                                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Expected</th>
                                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Actual</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Method</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Status</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Start Date</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {batches.map((batch) => (
                                                    <tr key={batch.id} className="hover:bg-green-50 transition-colors">
                                                        <td className="px-4 py-3 text-sm font-bold text-gray-900">{batch.batchNumber}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-700">
                                                            {batch.fieldName ? (
                                                                <span className="inline-flex items-center gap-1">
                                                                    <span className="font-medium">{batch.fieldName}</span>
                                                                    {batch.fieldCropType && <span className="text-xs text-gray-400">({batch.fieldCropType})</span>}
                                                                </span>
                                                            ) : <span className="text-gray-400">—</span>}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-700">
                                                            {batch.farmerName || <span className="text-gray-400">—</span>}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right">{Number(batch.manure_used_kg || 0).toLocaleString()} kg</td>
                                                        <td className="px-4 py-3 text-sm text-right">{Number(batch.carbon_materials_kg || 0).toLocaleString()} kg</td>
                                                        <td className="px-4 py-3 text-sm text-right">{Number(batch.water_added_liters || 0).toLocaleString()} L</td>
                                                        <td className="px-4 py-3 text-sm text-right font-semibold text-blue-700">{Number(batch.expected_output_kg || 0).toLocaleString()} kg</td>
                                                        <td className="px-4 py-3 text-sm text-right font-bold text-green-700">{Number(batch.actual_output_kg || batch.kgProduced || 0).toLocaleString()} kg</td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                                                                {batch.compost_method || 'Windrow'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3"><StatusBadge status={batch.status} /></td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">{batch.startDate}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Windrow Infrastructure */}
                                <div>
                                    <h4 className="text-md font-bold text-gray-900 mb-4">Windrow Infrastructure</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {windrows.map((windrow) => (
                                            <div key={windrow.id} className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-5 rounded-xl border-2 border-amber-200 shadow-md hover:shadow-lg transition-shadow">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h4 className="font-bold text-amber-900 text-lg">{windrow.windrow_number}</h4>
                                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                                        windrow.status === 'Available' ? 'bg-green-100 text-green-700' : 
                                                        windrow.status === 'In Use' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                                    }`}>
                                                        {windrow.status}
                                                    </span>
                                                </div>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex justify-between items-center p-2 bg-white/50 rounded-lg">
                                                        <span className="text-gray-600 font-medium">Dimensions:</span>
                                                        <span className="font-bold text-gray-900">{windrow.length_meters}m × {windrow.width_meters}m × {windrow.height_meters}m</span>
                                                    </div>
                                                    <div className="flex justify-between items-center p-2 bg-white/50 rounded-lg">
                                                        <span className="text-gray-600 font-medium">Volume:</span>
                                                        <span className="font-bold text-blue-700">{Number(windrow.volume_cubic_meters).toFixed(2)} m³</span>
                                                    </div>
                                                    <div className="flex justify-between items-center p-2 bg-white/50 rounded-lg">
                                                        <span className="text-gray-600 font-medium">Capacity:</span>
                                                        <span className="font-bold text-green-700">{Number(windrow.capacity_kg).toLocaleString()} kg</span>
                                                    </div>
                                                    {windrow.current_batch_number && (
                                                        <div className="mt-2 p-2 bg-blue-100 rounded-lg border border-blue-200">
                                                            <p className="text-xs text-blue-700 font-semibold">Current Batch: {windrow.current_batch_number}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'distribution' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-bold text-gray-900">Farmer Compost Distribution</h3>
                                    <button
                                        onClick={() => setShowDistributionModal(true)}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        Record Distribution
                                    </button>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Farmer</th>
                                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Farm Size</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Crop</th>
                                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Recommended</th>
                                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Received</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {distributions.map((dist) => (
                                                <tr key={dist.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{dist.farmer_name}</td>
                                                    <td className="px-4 py-3 text-sm text-right">{Number(dist.farm_size_hectares).toFixed(2)} ha</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{dist.crop_type}</td>
                                                    <td className="px-4 py-3 text-sm text-right text-blue-700 font-semibold">{Number(dist.recommended_compost_kg).toLocaleString()} kg</td>
                                                    <td className="px-4 py-3 text-sm text-right text-green-700 font-bold">{Number(dist.compost_received_kg).toLocaleString()} kg</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{dist.distribution_date}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {activeTab === 'analytics' && analytics && (
                            <div className="space-y-6">
                                <h3 className="text-lg font-bold text-gray-900">Production Analytics</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200">
                                        <p className="text-sm text-amber-700 font-semibold">Total Manure Collected</p>
                                        <p className="text-3xl font-bold text-amber-900 mt-2">{Number(analytics.total_manure_collected || 0).toLocaleString()} kg</p>
                                    </div>
                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border border-green-200">
                                        <p className="text-sm text-green-700 font-semibold">Total Carbon Materials</p>
                                        <p className="text-3xl font-bold text-green-900 mt-2">{Number(analytics.total_carbon_collected || 0).toLocaleString()} kg</p>
                                    </div>
                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200">
                                        <p className="text-sm text-blue-700 font-semibold">Compost Produced</p>
                                        <p className="text-3xl font-bold text-blue-900 mt-2">{Number(analytics.total_compost_produced || 0).toLocaleString()} kg</p>
                                    </div>
                                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border border-purple-200">
                                        <p className="text-sm text-purple-700 font-semibold">Farmers Served</p>
                                        <p className="text-3xl font-bold text-purple-900 mt-2">{Number(analytics.farmers_served || 0)}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                                        <h4 className="font-semibold text-gray-900 mb-4">Material Efficiency</h4>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Total Input</span>
                                                <span className="font-bold">{(Number(analytics.total_manure_collected || 0) + Number(analytics.total_carbon_collected || 0)).toLocaleString()} kg</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Compost Output</span>
                                                <span className="font-bold text-green-700">{Number(analytics.total_compost_produced || 0).toLocaleString()} kg</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Conversion Rate</span>
                                                <span className="font-bold">
                                                    {((Number(analytics.total_compost_produced || 0) / (Number(analytics.total_manure_collected || 0) + Number(analytics.total_carbon_collected || 0) || 1)) * 100).toFixed(1)}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                                        <h4 className="font-semibold text-gray-900 mb-4">Distribution Summary</h4>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Compost Distributed</span>
                                                <span className="font-bold text-blue-700">{Number(analytics.compost_distributed || 0).toLocaleString()} kg</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Revenue Generated</span>
                                                <span className="font-bold text-green-700">{formatCurrency(Number(analytics.revenue_generated || 0))}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Active Batches</span>
                                                <span className="font-bold">{Number(analytics.active_batches || 0)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'labor' && (
                            <StipendManagement stipends={stipends} onPaymentSuccess={fetchAllData} />
                        )}
                        {activeTab === 'sales' && (
                            <SurplusSalesTracker sales={salesData} batches={batches} onSaleAdded={fetchAllData} />
                        )}
                    </div>
                </div>

                {/* ── ANALYTICS (Manager only) ── */}
                {isManager && !loading && (
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <SparklesIcon className="w-5 h-5 text-green-600" />
                            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Production Analytics</h2>
                            <span className="text-xs text-gray-400">Project Manager view</span>
                        </div>
                        <AnalyticsPanel batches={batches} sales={salesData} summary={summary} formatCurrency={formatCurrency} />
                    </div>
                )}

            </div>

            {/* ── MODALS ── */}
            {showCreateModal && (
                <ProfessionalBatchModal onClose={() => setShowCreateModal(false)} onCreated={fetchAllData} />
            )}

            {selectedBatch && (
                <BatchDetailModal
                    batch={selectedBatch}
                    isOpen={!!selectedBatch}
                    onClose={() => setSelectedBatch(null)}
                />
            )}

            {qualityFormBatch && (
                <QualityControlForm
                    batch={qualityFormBatch}
                    isOpen={!!qualityFormBatch}
                    onClose={() => setQualityFormBatch(null)}
                    onSuccess={() => { setQualityFormBatch(null); fetchAllData(); }}
                />
            )}

            {laborModalBatch && (
                <LaborAssignmentModal
                    batchId={laborModalBatch.id}
                    batchNumber={laborModalBatch.batchNumber}
                    isOpen={!!laborModalBatch}
                    onClose={() => setLaborModalBatch(null)}
                    onSuccess={() => { setLaborModalBatch(null); fetchAllData(); }}
                />
            )}

            {showManureModal && (
                <AddManureSourceModal
                    onClose={() => setShowManureModal(false)}
                    onSuccess={() => { setShowManureModal(false); fetchAllData(); }}
                />
            )}

            {showDistributionModal && (
                <AddDistributionModal
                    onClose={() => setShowDistributionModal(false)}
                    onSuccess={() => { setShowDistributionModal(false); fetchAllData(); }}
                />
            )}
        </div>
    );
};

export default CompostPage;
